import React, { FC, useMemo } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { activeEnv } from '../../data/constants';
import { getBasePath } from '../../helpers/getBasePath';
import { getLocalizedUrl } from '../../helpers/getLocalizedUrl';
import { appLocales, DEFAULT_LOCALE } from '../../i18n';

type Props = {
  locale: string;
  pathname: string;
};

const Head: FC<Props & Queries.MetaFragment> = props => {
  const { meta, locale, pathname } = props;
  const { title, description, image, twitter } = meta || {};

  const schema = (meta as Record<string, unknown>)?.schema as Record<string, unknown> | undefined;

  const {
    site: {
      siteMetadata: { siteUrl },
    },
  } = useStaticQuery(graphql`
    query SiteData {
      site {
        siteMetadata {
          siteUrl
        }
      }
    }
  `);

  // Generate hreflang alternate URLs for all supported locales
  const hreflangTags = useMemo(() => {
    const basePath = getBasePath(pathname, locale);
    const locales = Object.keys(appLocales);

    return locales.map(localeCode => ({
      localeCode,
      url: getLocalizedUrl(siteUrl, basePath, localeCode),
    }));
  }, [pathname, locale, siteUrl]);

  // Get the default (English) URL for x-default
  const defaultUrl = useMemo(() => {
    const basePath = getBasePath(pathname, locale);
    return getLocalizedUrl(siteUrl, basePath, DEFAULT_LOCALE);
  }, [pathname, locale, siteUrl]);

  return (
    <>
      <html key="html" lang={locale} />

      <title key="title">{title}</title>
      <meta key="description" name="description" content={`${description}`} />

      <meta key="og:title" property="og:title" content={`${title}`} />
      <meta
        key="og:description"
        property="og:description"
        content={`${description}`}
      />
      <meta key="og:image" property="og:image" content={`${siteUrl}${image}`} />
      <meta
        key="og:image_secure"
        property="og:image:secure_url"
        content={`${siteUrl}${image}`}
      />
      <meta key="og:image_width" property="og:image:width" content="1200" />
      <meta key="og:image_height" property="og:image:height" content="630" />

      <meta
        key="twitter:card"
        name="twitter:card"
        content="summary_large_image"
      />
      <meta key="twitter:site" name="twitter:site" content={`@${twitter}`} />
      <meta key="twitter:title" name="twitter:title" content={`${title}`} />
      <meta
        key="twitter:description"
        name="twitter:description"
        content={`${description}`}
      />
      <meta
        key="twitter:image"
        name="twitter:image"
        content={`${siteUrl}${image}`}
      />

      <link key="canonical" rel="canonical" href={`${siteUrl}${pathname}`} />

      {/* Hreflang tags for all language versions */}
      {hreflangTags.map(({ localeCode, url }) => (
        <link
          key={`hreflang-${localeCode}`}
          rel="alternate"
          hrefLang={localeCode}
          href={url}
        />
      ))}
      {/* x-default for users without a language match */}
      <link
        key="hreflang-x-default"
        rel="alternate"
        hrefLang="x-default"
        href={defaultUrl}
      />

      {activeEnv !== 'production' && <meta name="robots" content="noindex" />}

      {/* JSON-LD Schema.org structured data */}
      {schema && Object.keys(schema).length > 0 && (
        <script
          key="json-ld"
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      )}
    </>
  );
};

// TODO: use gatsby image here..
// All Json files' meta image MUST be located in assets directory for this to work
// GQL is
/*
  image {
    publicURL
  }
*/
export const query = graphql`
  fragment Meta on Json {
    meta {
      title
      description
      image {
        publicURL
      }
      twitter
      schema
    }
  }
`;

export default Head;
